import React, {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

interface SheetModalProps {
  show: boolean
  onHide: () => void
  title: string
  icon?: string
  elementIcon?: React.ReactNode
  children: React.ReactNode
  showCloseButton?: boolean
  /** Sheet width cap; the sheet still shrinks to 100% on narrow viewports. */
  maxWidth?: string
  /** Extra class(es) on the sheet card, for surface restyling by the host. */
  sheetClassName?: string
  /**
   * Squeeze the page instead of dimming it: while open, `sheet-push-open` is set
   * on <body> with `--sheet-push-width` = maxWidth, and the backdrop is removed
   * from hit-testing so the page beside the sheet keeps scrolling (a transparent
   * backdrop still swallowed wheel events; a long review page was stuck). No
   * click-to-close in this mode — the sheet's own close/cancel controls do that.
   * Hosts opt in and provide the matching CSS (see .onboarding-main-content in
   * _onboardingwizard.scss).
   */
  push?: boolean
}

export const SheetModal: React.FC<SheetModalProps> = ({
  show,
  onHide,
  title,
  icon = 'fa-calendar-alt',
  children,
  elementIcon,
  showCloseButton = false,
  maxWidth = '560px',
  sheetClassName,
  push = false,
}) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setIsOpen(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsOpen(false)
    }
  }, [show])

  // Push mode: squeeze the host page for the sheet's width while it is open.
  useEffect(() => {
    if (!push || !show) return
    document.body.classList.add('sheet-push-open')
    document.body.style.setProperty('--sheet-push-width', maxWidth)
    return () => {
      document.body.classList.remove('sheet-push-open')
      document.body.style.removeProperty('--sheet-push-width')
    }
  }, [push, show, maxWidth])

  if (!show) return null

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      className='position-fixed'
      style={{
        inset: 0,
        zIndex: 1055,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        // Push mode: the wrapper and backdrop let pointer/wheel events through to the
        // page; only the sheet itself catches them (see below).
        pointerEvents: push ? 'none' : 'auto',
      }}
    >
      {/* Backdrop overlay */}
      <div
        className='position-fixed frequency-edit-modal-backdrop'
        onClick={push ? undefined : onHide}
        style={{
          inset: 0,
          background: 'var(--bs-backdrop-bg)',
          zIndex: 1050,
          opacity: push ? 0 : isOpen ? 0.3 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: push ? 'none' : 'auto',
        }}
      />
      {/* Sheet container */}
      <div
        className={`card card-xl-stretch frequency-edit-modal-sheet${
          sheetClassName ? ` ${sheetClassName}` : ''
        }`}
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          maxWidth,
          zIndex: 1060,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          margin: 0,
          borderRadius: 0,
          pointerEvents: 'auto',
        }}
      >
        <div className='card-header d-flex align-items-center justify-content-between border-bottom'>
          <div className='d-flex align-items-center'>
            {icon && <i className={`fas ${icon} me-2`}></i>}
            {elementIcon && <span className='me-2'>{elementIcon}</span>}
            <span className='fw-bold'>{title}</span>
          </div>
          {showCloseButton && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light'
              onClick={onHide}
              aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
            >
              <i className='fas fa-times'></i>
            </button>
          )}
        </div>
        <div
          className='card-body pt-0'
          style={{
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
